import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import {
    Box,
    Divider,
    Flex,
    IconButton,
    LabelTag,
    Link,
    Popover,
    Sheet,
} from '@iris/react-components';
import { XMarkIcon } from '@iris/react-icons';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { LoadingSpinner, Text } from 'reaxl';
import { sendClick } from 'reaxl-analytics';

import { aiAssistantEntryPointClick } from '@/analytics/aiAssistantEvents';

import activeInteractionDuck from '@/ducks/activeInteractionDuck.js';
import listingsAgentDuck from '@/ducks/listingsAgentDuck';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck';

import SearchInput from '@/components/SearchInput';

function ShoppingAssistantPopover({
    handleAssistantClose = () => { },
    headingText = 'What would you like to do?',
    show = false,
    suggestedActions = null,
    trigger,
    ...rest
}) {

    const dispatch = useDispatch();

    const device = useDevice();
    const isXs = _get(device, 'is.xs', false);

    const messages = useSelector(listingsAgentDuck.selectors.getMessages);
    const isLoading = useSelector(listingsAgentDuck.selectors.isLoading);

    const vdpListing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const [searchQuery, setSearchQuery] = useState('');
    const chatContainerRef = useRef(null);

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    const {
        ai_assistant_entry_point: [isAIAssistantEntryPointEnabled, { destination: aiEndpoint }],
        suggested_keyword: [enableKeyword, { keyword_placeholder: keywordPlaceholder }],
        shopping_assistant: [, { listings_agent: enableListingsAgent }],
    } = useFeatures(['ai_assistant_entry_point', 'suggested_keyword', 'shopping_assistant']);

    const onHide = useCallback(() => {
        handleAssistantClose();
    }, [handleAssistantClose]);

    const handleSearchInputChange = useCallback((event) => {
        setSearchQuery(event.target.value);
    }, []);

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();

        const userMessage = searchQuery.trim();

        // Only proceed if we have a non-empty message
        if (userMessage) {
            // Clear input immediately
            setSearchQuery('');

            await dispatch(listingsAgentDuck.creators.sendUserMessage(userMessage));
        }
    }, [searchQuery, dispatch]);

    const searchForm = (
        <form onSubmit={handleSubmit}>
            <SearchInput
                placeholder="Ask Anything"
                data-cmp="shopping-assistant-search"
                value={searchQuery}
                onChange={handleSearchInputChange}
                autoFocus
            />
        </form>
    );

    const keywordInput = enableKeyword && (
        <SearchInput
            placeholder={keywordPlaceholder}
            data-cmp="cntnr-kywrd"
            onFocus={() => {
                dispatch(activeInteractionDuck.creators.setKeys({
                    showKeywordSearchModal: true,
                }));
                handleAssistantClose();
            }}
            aria-label={keywordPlaceholder}
        />
    );

    const renderHtmlResponse = (htmlContent) => (
        <div className="iris:my-4 w-100">
            <Text size={200}>
                {
                    <div
                        className="shopping-assistant-response"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />
                }
            </Text>
        </div>
    );

    const renderMessage = (message) => {
        if (message.source === 'user') {
            return (
                <div
                    key={message.id}
                    className="iris:flex iris:justify-end w-100 iris:mb-3"
                >
                    <Box
                        className="iris:p-3 iris:bg-(--base-color-secondary-100) iris:rounded-3xl"
                    >
                        <Text size={200}>{message.content}</Text>
                    </Box>
                </div>
            );
        }

        return (
            <div key={message.id}>
                {renderHtmlResponse(message.content)}
            </div>
        );
    };

    const renderChatDialog = () => {
        const hasMessages = messages.length > 0;

        if (!hasMessages && !isLoading) {
            return null;
        }

        return (
            <>
                {messages.map(renderMessage)}

                {(isLoading) && (
                    <div className="iris:my-4 w-100">
                        <LoadingSpinner className="margin-top-1 margin-bottom-2" />
                    </div>
                )}
            </>
        );
    };

    const handleAiAssistantClick = (event) => {
        sendClick(aiAssistantEntryPointClick, event, {
            placement: 'shopping-assistant-popover',
            linkText: 'Ask Autotrader',
            url: `/${aiEndpoint}`,
            cmp: 'text-link::try-our-new-AI-shopping-assistant',
            listingId: vdpListing?.id,
        });
    };

    const content = (
        <div className="iris:flex iris:flex-col iris:grow gap-3">
            <div
                ref={chatContainerRef}
                className="iris:flex iris:flex-wrap"
                style={{ maxHeight: '60vh', overflow: 'hidden', overflowY: 'auto' }}
            >
                {renderChatDialog()}

                {suggestedActions && (
                    <Flex
                        wrap="wrap"
                        className="iris:mt-3"
                    >
                        {suggestedActions}
                    </Flex>
                )}

                {isAIAssistantEntryPointEnabled && (
                    <Flex className="iris:mt-3 iris:mx-1">
                        <Link
                            className="iris:mr-2"
                            href={`/${aiEndpoint}`}
                            size="sm"
                            target="_blank"
                            weight="bold"
                            onClick={handleAiAssistantClick}
                        >
                            Let AI drive your car search. Ask Autotrader.
                        </Link>
                        <LabelTag text="BETA" />
                    </Flex>
                )}
            </div>
        </div>
    );

    if (isXs) {
        return (
            <div data-cmp="shopping-assistant-popover">
                <Sheet
                    desktopPosition="center"
                    fixedButtons
                    footerContent={enableListingsAgent ? searchForm : keywordInput}
                    hasHeader
                    modal={false}
                    onOpenChange={(open) => {
                        if (!open) {
                            onHide();
                        }
                    }}
                    open={show}
                    title={headingText}
                    trigger={trigger}
                    {...rest}
                >
                    {content}
                </Sheet>
            </div>
        );
    }

    // Desktop: Use Iris Popover component
    return (
        <Popover
            id="shopping-assistant-popover"
            data-cmp="shopping-assistant-popover"
            isOpen={show}
            trigger={trigger}
        >
            <div className="iris:flex iris:items-center iris:relative">
                <Text
                    className="iris:grow iris:text-center"
                    data-cmp="shopping-assist-dt-heading"
                    weight="bold"
                >
                    {headingText}
                </Text>
                <IconButton
                    className="iris:absolute"
                    style={{ right: 0 }}
                    onClick={handleAssistantClose}
                    icon={<XMarkIcon />}
                    size="sm"
                />
            </div>

            <Divider className="iris:my-3" />

            {content}

            <Divider className="iris:my-3" />

            {enableListingsAgent ? searchForm : keywordInput}
        </Popover>
    );
}
ShoppingAssistantPopover.propTypes = {
    /**
     * if true, the component will be shown
     * @default false
     */
    show: PropTypes.bool,
    /**
     * A group of CTA's to be rendered in the popover
     */
    suggestedActions: PropTypes.node,
    /**
     * The text to be displayed in the header of the popover
     */
    headingText: PropTypes.string,
    /**
     * Function to handle closing the assistant
     */
    handleAssistantClose: PropTypes.func,
    /**
     * Trigger element for the sheet
     */
    trigger: PropTypes.node,
};

export default React.memo(ShoppingAssistantPopover, _isEqual);
